import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, Building2, UserPlus, Shield } from 'lucide-react';
import Logo from '@/components/Logo';

const CompanyAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is company admin (role='admin' with companyId)
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/');
      return;
    }

    const userData = JSON.parse(userStr);
    // Must be admin with a companyId (not super admin)
    if (userData.role !== 'admin' || !userData.companyId) {
      navigate('/dashboard');
      return;
    }

    setUser(userData);
  }, [navigate, location]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-xl">
        <div className="p-6 border-b-4" style={{ borderColor: '#FF6B35' }}>
          <div className="cursor-pointer mb-3" onClick={() => navigate('/dashboard')} data-testid="company-admin-sidebar-logo">
            <Logo size="md" showText={true} textColor="#1F2937" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => navigate('/company-admin/users')} data-testid="company-admin-panel-link">Company Admin Panel</h2>
          <p className="text-sm text-gray-600 mt-1 select-text" data-testid="company-admin-email-text">{user.email}</p>
        </div>

        {/* Navigation */}
        <nav className="p-4">
          <div>
            <p className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Team Management
            </p>

            {/* Team Members */}
            <button
              onClick={() => navigate('/company-admin/users')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/company/users') || isActive('/company-admin/users')
                  ? 'bg-orange-100 text-orange-700 font-medium'
                  : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
              }`}
            >
              <Users className="h-5 w-5" />
              <span>Team Members</span>
            </button>

            {/* Add Team Member */}
            <button
              onClick={() => navigate('/company/create-user')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/company/create-user') || isActive('/company-admin/create-user')
                  ? 'bg-orange-100 text-orange-700 font-medium'
                  : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
              }`}
            >
              <UserPlus className="h-5 w-5" />
              <span>Add Team Member</span>
            </button>

            {/* Auth Activity */}
            <button
              onClick={() => navigate('/company-admin/auth-activity')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/company-admin/auth-activity')
                  ? 'bg-orange-100 text-orange-700 font-medium'
                  : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
              }`}
            >
              <Shield className="h-5 w-5" />
              <span>Auth Activity</span>
            </button>
          </div>

          {/* Back to Dashboard - same position as Admin Console */}
          <div className="mt-8 pt-8 border-t">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full p-3 rounded-lg text-white hover:opacity-90 transition-all"
              style={{ background: '#FF6600' }}
              data-testid="company-admin-back-to-dashboard"
            >
              Back to Dashboard
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

export default CompanyAdminLayout;
